/**
 * Transporte WebSocket del cliente (M1-infra) **con reconexión automática**.
 *
 * Vive en el cliente (usa el `WebSocket` nativo del navegador, que es un global
 * del DOM → no puede ir en `@tak/sim`, que se compila DOM-free). Conecta un
 * `PredictiveClient` a un servidor autoritativo real: predice el input local y
 * lo envía; reconcilia con cada snapshot que llega.
 *
 * El estado a renderizar es `client.game` (un GameState), igual que en el
 * loopback → la capa de render de main.ts no distingue local de online.
 *
 * UX-2 · **Estado de conexión observable**. Una caída de red no es un caso raro
 * en móvil: es lo normal (túnel, cambio de wifi a datos). Aquí se modela como un
 * estado explícito (`ConnState`) que la UI pinta, en vez de dejar la partida
 * congelada sin explicación:
 *
 *   connecting → connected → (se cae) → reconnecting → connected
 *                                                   └→ disconnected (se agotaron los reintentos)
 *                            → rejected (el servidor nos cerró la puerta: contraseña/sala llena/expulsión)
 *
 * La reconexión reabre el socket contra la MISMA sala (`?room=<id>`) con backoff
 * exponencial y jitter. `rejected` no reintenta: si la contraseña es mala o te
 * expulsaron, insistir solo hace ruido.
 */
import { PredictiveClient, ClientOptions, InputState, RoomPhase, ControlAction, PeerInfo, TeamId, NoticeKind, ReportReason, mapById, decodeServer, encode, adoptTeams, DEFAULT_TEAM_NAMES } from "@tak/sim";

/** Opciones de una acción de control (mapa/reglas + acciones de admin dirigidas). */
export interface ControlOpts {
  mapId?: string;
  goalLimit?: number;
  timeLimit?: number;
  targetSlot?: number;
  team?: TeamId | "spec";
  teamNames?: [string, string];
}

/** Estado de la conexión, tal cual lo pinta la UI (UX-2). */
export type ConnState = "connecting" | "connected" | "reconnecting" | "disconnected" | "rejected";

/** Mensaje de chat ya difundido por la sala (emisor identificado por el servidor). */
export interface ChatLine {
  slot: number;
  name: string;
  team?: TeamId | "spec";
  text: string;
}

/**
 * Backoff de reconexión: rápido al principio (la mayoría de cortes duran menos
 * de un segundo) y luego espaciado para no martillear un servidor caído. El
 * primer reintento sale a ~300 ms, así el caso normal se resuelve MUY por
 * debajo del objetivo de 5 s de la Fase 0.
 */
const RETRY_DELAYS_MS = [300, 600, 1200, 2400, 4000, 4000, 6000, 6000, 8000, 8000];
/** Intentos antes de rendirse y pasar a `disconnected`. */
const MAX_RETRIES = RETRY_DELAYS_MS.length;

/** Códigos de cierre del servidor (4000+) que significan "no vuelvas". */
const FATAL_CLOSE_MIN = 4000;
/**
 * "La sala está llena" NO es un veredicto definitivo cuando uno ya estaba
 * dentro: al reconectar, quien ocupa tu sitio suele ser tu propia conexión
 * anterior, que el servidor aún no ha dado por muerta. Rendirse ahí dejaba al
 * jugador mirando una partida congelada para siempre — reproducido: el que
 * vuelve recibe el rechazo y su reloj se queda a 0:00 mientras el del rival
 * corre. Se reintenta como cualquier corte, con la misma espera creciente.
 *
 * Lo que sí es definitivo: contraseña incorrecta (4003) y expulsión (4010).
 */
const CLOSE_ROOM_FULL = 4009;
/** Desahucio por silencio (NET-2): el servidor cree que estábamos muertos.
 *  Volver es exactamente lo que hay que hacer. */
const CLOSE_GHOST = 4008;
/**
 * EXPULSIÓN por el admin de la sala.
 *
 * Necesita su propio código en el cliente porque es el único cierre "de
 * autoridad" que puede llegar CON la partida ya empezada, y entonces no basta
 * con marcar la conexión como rechazada: hay que sacar a la persona de un
 * partido que ya no es suyo. Sin esto se quedaba mirando la cancha congelada
 * con un cartel de "desconectado", sin saber que la habían echado ni cómo
 * salir de ahí.
 */
const CLOSE_KICKED = 4010;

export class WsNetClient {
  readonly client: PredictiveClient;
  private ws: WebSocket | null = null;
  /** Construye la URL de conexión; recibe la sala a la que volver (o null). */
  private readonly makeUrl: (roomId: string | null) => string;
  private welcomed = false;
  /** true una vez completada la primera bienvenida (ya hubo partida). */
  private everWelcomed = false;
  private retries = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  /** true cuando `close()` fue decisión nuestra: no hay que reconectar. */
  private closedByUs = false;
  private state: ConnState = "connecting";

  localSlot = 0;
  /** Id de la sala asignada por el servidor (para el link de invitación). */
  roomId: string | null = null;
  /** Fase de la sala (lobby/countdown/playing/paused/over), según el servidor. */
  phase: RoomPhase = "playing";
  /** Segundos que faltan para el saque en la fase `countdown` (GP-2). Lo cuenta
   *  el SERVIDOR: si cada cliente lo hiciera por su cuenta, uno empezaría a
   *  jugar antes que el otro. */
  countdown = 0;
  /** Ocupantes actuales de la sala (slot + nombre), enviados por el servidor. */
  peers: PeerInfo[] = [];
  /** Quién está MIRANDO la sala y dio nombre (sin slot ni cuerpo). */
  spectators: string[] = [];
  /** Cuánta gente mira en total, con nombre o sin él (el fondo del menú es anónimo). */
  spectatorCount = 0;
  /** Nombres de los equipos que fijó el admin (los mismos para todos). */
  teamNames: [string, string] = [...DEFAULT_TEAM_NAMES] as [string, string];
  onWelcome: (() => void) | null = null;
  onState: ((phase: RoomPhase) => void) | null = null;
  /** Cambió la lista de jugadores de la sala (alguien entró o salió). */
  onPeers: ((peers: PeerInfo[]) => void) | null = null;
  /** Llegó un mensaje de chat de la sala (incluido el eco del propio). */
  onChat: ((line: ChatLine) => void) | null = null;
  /** NET-4 · El servidor anuncia un reinicio/mantenimiento con cuenta atrás. */
  onNotice: ((notice: { kind: NoticeKind; seconds: number; message?: string }) => void) | null = null;
  /** El servidor cerró la conexión antes del welcome (unión rechazada). El
   *  motivo viene en el `reason` del cierre: "bad-password" / "room-full". */
  onRejected: ((reason: string) => void) | null = null;
  /** El admin de la sala nos expulsó (cierre 4010), estuviéramos jugando o no. */
  onKicked: (() => void) | null = null;
  /** Cambió el estado de la conexión (UX-2): la UI lo pinta tal cual. */
  onConnState: ((state: ConnState, attempt: number) => void) | null = null;

  constructor(makeUrl: string | ((roomId: string | null) => string), opts: ClientOptions = { players: 2, localSlot: 0 }) {
    // autoLead: red real → el cliente corre por delante del servidor para que
    // sus inputs no lleguen tarde y se descarten (ver PredictiveClient).
    // soloStart: hasta que el servidor diga quién hay dentro, en la cancha solo
    // estás tú. Sin esto, una sala de N nacía con N discos y en un 1v0 se veía
    // un "rival" de relleno durante las primeras décimas de segundo.
    this.client = new PredictiveClient({ ...opts, autoLead: true, soloStart: true });
    this.makeUrl = typeof makeUrl === "string" ? () => makeUrl : makeUrl;
    this.open();
  }

  /* ---------------- ciclo de vida del socket ---------------- */

  private setState(next: ConnState): void {
    if (this.state === next) return;
    this.state = next;
    this.onConnState?.(next, this.retries);
  }

  /** Estado actual de la conexión (UX-2). */
  get connState(): ConnState {
    return this.state;
  }

  /** Intento de reconexión en curso (0 = ninguno). Para el texto de la UI. */
  get retryAttempt(): number {
    return this.retries;
  }

  private open(): void {
    this.welcomed = false;
    this.setState(this.everWelcomed ? "reconnecting" : "connecting");
    const ws = new WebSocket(this.makeUrl(this.roomId));
    this.ws = ws;

    ws.onopen = (): void => {
      // Socket abierto ≠ dentro de la sala: eso lo dice el `welcome`.
      if (this.everWelcomed) {
        // Reconexión: el socket nuevo trae otro reloj de servidor. Hay que
        // olvidar tick/pendientes/buffer o los snapshots se descartarían por
        // "viejos" y la partida quedaría congelada pese a estar conectada.
        this.client.resync();
      }
    };

    ws.onmessage = (ev: MessageEvent): void => {
      const msg = decodeServer(typeof ev.data === "string" ? ev.data : String(ev.data));
      if (!msg) return;
      if (msg.t === "welcome") {
        // El slot se adopta ANTES de dimensionar el mundo: `configurePlayers` lo
        // recrea con un solo sitio ocupado (`soloStart`), y ese sitio tiene que
        // ser el nuestro — si no, el disco que se dibuja es el de otro.
        this.localSlot = msg.slot;
        this.client.localSlot = msg.slot; // adopta el slot asignado por el servidor
        // Ajusta el mundo a la capacidad real de la sala (N jugadores): los
        // snapshots traerán N cuerpos.
        if (msg.players != null) this.client.configurePlayers(msg.players);
        this.roomId = msg.roomId ?? this.roomId;
        this.applyRoomConfig(msg.mapId, msg.goalLimit, msg.timeLimit);
        this.welcomed = true;
        this.everWelcomed = true;
        this.retries = 0;
        this.setState("connected");
        this.onWelcome?.();
        // El slot puede haber CAMBIADO sin reconectar (pasar a público y
        // volver): la sala manda una bienvenida nueva. Quien pinta el roster
        // necesita enterarse, porque "cuál de estos soy yo" acaba de cambiar y
        // la lista que ya tenía sigue siendo la buena.
        this.onPeers?.(this.peers);
      } else if (msg.t === "snapshot") {
        this.client.reconcile(msg.snap);
        // La fase autoritativa viaja en cada snapshot: si diverge de la que
        // creíamos, se corrige aquí (el `state` del alta puede haberse perdido o
        // haber cambiado sin que nos llegara la difusión). Solo se notifica el
        // CAMBIO, que es lo que la UI necesita.
        if (msg.snap.countdown !== undefined) this.countdown = msg.snap.countdown;
        if (msg.snap.phase !== undefined && msg.snap.phase !== this.phase) {
          this.phase = msg.snap.phase;
          this.onState?.(msg.snap.phase);
        }
      } else if (msg.t === "state") {
        this.applyRoomConfig(msg.mapId, msg.goalLimit, msg.timeLimit);
        this.phase = msg.phase;
        if (msg.teamNames) this.teamNames = [msg.teamNames[0], msg.teamNames[1]];
        this.onState?.(msg.phase);
      } else if (msg.t === "peers") {
        this.peers = msg.peers;
        this.spectators = msg.spectators ?? [];
        this.spectatorCount = msg.spectatorCount ?? this.spectators.length;
        // De qué equipo es cada slot lo decide la SALA, y hasta ahora el mundo
        // predicho no se enteraba: nacía con el reparto por defecto (0,1,0,1…)
        // y ahí se quedaba. Los colores salían bien —el render los saca de esta
        // misma lista— así que no se notaba, hasta que hubo reglas que dependen
        // del equipo: con el saque, un cliente que se creía del bando contrario
        // se encerraba en la mitad equivocada mientras el servidor le dejaba
        // jugar. Se adopta sin recolocar a nadie: las posiciones son del
        // servidor (ver `adoptTeams`).
        const porSlot: (TeamId | undefined)[] = [];
        for (const p of msg.peers) if (p.team === 0 || p.team === 1) porSlot[p.slot] = p.team;
        adoptTeams(this.client.game, porSlot);
        this.onPeers?.(msg.peers);
      } else if (msg.t === "chat") {
        this.onChat?.({ slot: msg.slot, name: msg.name, team: msg.team, text: msg.text });
      } else if (msg.t === "notice") {
        // NET-4 · El corte que viene a continuación está anunciado: la UI lo
        // pinta para que no se lea como una caída.
        this.onNotice?.({ kind: msg.kind, seconds: msg.seconds, message: msg.message });
      }
    };

    ws.onclose = (ev: CloseEvent): void => {
      if (this.closedByUs) return;
      // Estábamos DENTRO y nos cierran por "sala llena" o por creernos muertos:
      // las dos son situaciones transitorias de una reconexión, no un veredicto.
      // Se reintenta (ver la nota de `CLOSE_ROOM_FULL`).
      const transient = this.everWelcomed
        && (ev.code === CLOSE_ROOM_FULL || ev.code === CLOSE_GHOST);
      // Cierre "de autoridad" (4000+): contraseña mala, sala llena, expulsión.
      // Reintentar sería inútil y molesto — el servidor ya dijo que no.
      if (ev.code >= FATAL_CLOSE_MIN && !transient) {
        this.setState("rejected");
        // Expulsión: se avisa SIEMPRE, se estuviera jugando o no. Es la única
        // salida decente — quien te echó no va a dejarte volver, así que quedarse
        // en la pantalla del partido es dejar a alguien encerrado.
        if (ev.code === CLOSE_KICKED) this.onKicked?.();
        // El rechazo de UNIÓN solo se notifica si nunca llegamos a entrar; si ya
        // estábamos jugando, lo cuenta el estado de conexión.
        else if (!this.everWelcomed) this.onRejected?.(ev.reason || String(ev.code));
        return;
      }
      this.scheduleRetry();
    };

    // `onerror` siempre va seguido de `onclose` en el navegador: la reconexión
    // se gestiona ahí y no hace falta duplicarla aquí.
    ws.onerror = (): void => { /* el cierre subsiguiente decide qué hacer */ };
  }

  private scheduleRetry(): void {
    if (this.retryTimer !== null) return;
    if (this.retries >= MAX_RETRIES) {
      this.setState("disconnected");
      return;
    }
    const base = RETRY_DELAYS_MS[Math.min(this.retries, RETRY_DELAYS_MS.length - 1)];
    // Jitter ±25%: si se cae el servidor, sus clientes no vuelven todos a la vez.
    const delay = Math.round(base * (0.75 + Math.random() * 0.5));
    this.retries++;
    this.setState(this.everWelcomed ? "reconnecting" : "connecting");
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (!this.closedByUs) this.open();
    }, delay);
  }

  /** true = dentro de la sala y con socket vivo (se puede jugar). */
  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN && this.welcomed;
  }

  /* ---------------- envío ---------------- */

  /** Predice localmente y envía el input al servidor autoritativo. Espera al
   *  `welcome` para conocer su slot antes de predecir (matchmaking 2 humanos). */
  send(input: InputState): void {
    if (!this.welcomed) return;
    const cmd = this.client.predict(input);
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(encode({ t: "input", cmd }));
  }

  /** Reconfigura la cancha/reglas del cliente si la sala usa otras (join a un
   *  mapa distinto, o restart del host con nuevo mapa/reglas). */
  private applyRoomConfig(mapId?: string, goalLimit?: number, timeLimit?: number): void {
    if (!mapId) return;
    const g = this.client.game;
    const rulesChanged =
      (goalLimit != null && goalLimit !== g.rules.goalLimit) ||
      (timeLimit != null && timeLimit !== g.rules.timeLimit);
    if (mapId === g.mapId && !rulesChanged) return;
    this.client.reconfigure(mapById(mapId), {
      goalLimit: goalLimit ?? g.rules.goalLimit,
      timeLimit: timeLimit ?? g.rules.timeLimit,
    });
  }

  /** Envía una acción de control de sala (solo la aplica el servidor si eres admin). */
  control(action: ControlAction, opts: ControlOpts = {}): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(encode({ t: "control", action, ...opts }));
  }

  /**
   * Envía un mensaje de chat a la sala. Devuelve `true` si salió por el socket.
   *
   * El `false` importa: quien llama lo usa para saber si debe pintar el mensaje
   * por su cuenta. Si el mensaje se fue al servidor, el texto que se pinta será
   * el que vuelva difundido (ya moderado y confirmado); si no llegó a salir, no
   * hay eco que esperar y la UI debe decirlo en vez de fingir que se envió.
   */
  sendChat(text: string): boolean {
    if (!this.welcomed || this.ws?.readyState !== WebSocket.OPEN) return false;
    this.ws.send(encode({ t: "chat", text }));
    return true;
  }

  /**
   * SEC-3 · Reporta a un ocupante de la sala. Devuelve `true` si salió.
   *
   * Se manda el SLOT y no una identidad: el cliente no tiene por qué conocer
   * ninguna, y dársela sería darle algo con lo que seguir a esa persona. El
   * servidor traduce el slot al ocupante actual.
   */
  report(targetSlot: number, reason: ReportReason): boolean {
    if (!this.welcomed || this.ws?.readyState !== WebSocket.OPEN) return false;
    this.ws.send(encode({ t: "report", targetSlot, reason }));
    return true;
  }

  /**
   * Anuncia que este jugador tiene el chat abierto (o que lo cerró).
   *
   * Es un booleano, no el borrador: lo que viaja es "está escribiendo", que es
   * lo que la sala necesita para pintar los tres puntitos sobre su disco y
   * entender por qué lleva un momento quieto.
   */
  setTyping(on: boolean): void {
    if (this.welcomed && this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(encode({ t: "typing", on }));
    }
  }

  /**
   * Cambia el nombre visible y/o el icono del disco SIN reconectar.
   *
   * El nombre llegaba solo en la query del socket, así que corregir una errata
   * costaba salirse de la sala. El servidor lo modera igual que al entrar y lo
   * difunde en el roster.
   */
  setProfile(p: { name?: string; icon?: string }): void {
    if (!this.welcomed || this.ws?.readyState !== WebSocket.OPEN) return;
    this.ws.send(encode({ t: "profile", ...p }));
  }

  /** Reporta el RTT medido (ms) al servidor para el ping por usuario del roster. */
  reportPing(ms: number): void {
    if (this.welcomed && this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(encode({ t: "ping", ms: Math.round(ms) }));
    }
  }

  /** Reintenta YA (botón "reintentar" de la UI cuando se agotaron los intentos). */
  retryNow(): void {
    if (this.closedByUs) return;
    if (this.retryTimer !== null) { clearTimeout(this.retryTimer); this.retryTimer = null; }
    this.retries = 0;
    this.open();
  }

  /**
   * Tira la conexión y vuelve a entrar a la MISMA sala.
   *
   * Es la salida de emergencia para un estado que, sin ella, no se arregla
   * solo: socket vivo pero la partida sin avanzar (el servidor no confirma
   * inputs ni manda estado nuevo). Puede pasar por una sala que quedó en una
   * fase que el cliente no vio, o porque el proceso del servidor se quedó sin
   * avanzar su reloj. Reconectar rehace el saludo entero —slot, fase, cancha,
   * reloj— y es lo único que recupera el caso desde el lado del cliente.
   */
  forceRejoin(): void {
    if (this.closedByUs) return;
    if (this.retryTimer !== null) { clearTimeout(this.retryTimer); this.retryTimer = null; }
    this.welcomed = false;
    const old = this.ws;
    this.ws = null;
    if (old) {
      // Se desengancha antes de cerrar: este cierre es nuestro y no debe
      // disparar la lógica de reconexión por caída.
      old.onclose = null;
      old.onmessage = null;
      old.onerror = null;
      try { old.close(); } catch { /* ya estaba cerrado */ }
    }
    this.retries = 0;
    this.open();
  }

  close(): void {
    this.closedByUs = true;
    if (this.retryTimer !== null) { clearTimeout(this.retryTimer); this.retryTimer = null; }
    this.setState("disconnected");
    this.ws?.close();
  }
}
